import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { ModuleReference } from "../../../../model/CertificationDataContextModel";
import {
  CourseForSignupViewModel,
  ExamSessionForSignupViewModel,
} from "../../../../model/ExamSignup/ExamSessionForSignupViewModel";
import { ModuleSelectionViewModel } from "../../../../model/ExamSignup/ModuleSelectionViewModel";
import { GetTranslatedValue } from "../../../../model/Shared/TranslatedValue";
import TranslatedMessage from "../../../translations/TranslatedMessage";
import { ModuleSelectorRow, ModuleSelectorRowProps } from "./ModuleSelectorRow";

type ModuleSelectorProps = {
  allAvailableCourses: CourseForSignupViewModel[];
  availableExamSessions: ExamSessionForSignupViewModel[];
  selection: ModuleSelectionViewModel[];
  referenceModules: ModuleReference[];
  onModuleSelected: (module: ModuleSelectionViewModel) => void;
};

const ModuleSelector: React.FunctionComponent<ModuleSelectorProps> = ({
  allAvailableCourses,
  availableExamSessions,
  selection,
  referenceModules,
  onModuleSelected,
}) => {
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [selectedExamSessionId, setSelectedExamSessionId] = useState<string>("");

  const handleCourseChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedCourseId(event.target.value as string);
    setSelectedExamSessionId("");
  };

  const handleExamSessionChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedExamSessionId(event.target.value as string);
  };

  const filteredExamSessions = availableExamSessions.filter(
    (s) => s.courses.find((c) => c.id === selectedCourseId) !== undefined
  );

  const createFilteredModuleRow = () => {
    // just take everything
    const rows = availableExamSessions.reduce((modules, examSession) => {
      examSession.courses.forEach((course) => {
        course.modules.forEach((module) => {
          modules.push({
            examSessionId: examSession.id,
            examSessionKey: examSession.sessionKey,
            courseId: course.id,
            courseName: course.name,
            module,
            rowButtonAction: onModuleSelected,
          });
        });
      });
      return modules;
    }, [] as ModuleSelectorRowProps[]);

    // then filter on current selection
    const currentExamsSelection = getSelectionExamSessionExams();
    return rows.filter(
      (row) =>
        isAllowedWithFilters(row) &&
        isNotAlreadySelected(row) &&
        hasNoExamSessionModuleOverlap(row) &&
        hasNoExamSessionExamOverlap(row, currentExamsSelection)
    );
  };

  const getSelectionExamSessionExams = () =>
    selection.reduce((exams, current) => {
      const refModule = referenceModules.find(
        (m) => m.examSessionId === current.examSessionId && m.courseId === current.courseId && m.id === current.moduleId
      );
      if (refModule) {
        exams = exams.concat(refModule.exams.map((e) => ({ examSessionId: current.examSessionId, examId: e.id })));
      }
      return exams;
    }, [] as { examSessionId: string; examId: string }[]);

  const isAllowedWithFilters = (row: ModuleSelectorRowProps) =>
    selectedExamSessionId !== "" && row.courseId === selectedCourseId && row.examSessionId === selectedExamSessionId;

  const isNotAlreadySelected = (row: ModuleSelectorRowProps) =>
    selection.find(
      (s) => s.courseId === row.courseId && s.examSessionId === row.examSessionId && s.moduleId === row.module.id
    ) === undefined;

  const hasNoExamSessionModuleOverlap = (row: ModuleSelectorRowProps) =>
    selection.find((s) => s.examSessionId === row.examSessionId && s.moduleId === row.module.id) === undefined;

  const hasNoExamSessionExamOverlap = (
    row: ModuleSelectorRowProps,
    currentExamSelection: { examSessionId: string; examId: string }[]
  ) => {
    const hasOverlapingExams = row.module.exams.some((e) =>
      currentExamSelection.some((s) => s.examId === e.id && s.examSessionId === row.examSessionId)
    );
    return !hasOverlapingExams;
  };

  const moduleRowProps = createFilteredModuleRow();

  return (
    <Grid container item spacing={4}>
      <Grid item xs={12}>
        <Typography variant="body1">
          <TranslatedMessage id="startCertification.moduleSelectionStep.helperText" />
        </Typography>
      </Grid>

      <Grid container item xs={12} spacing={4}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>
              <TranslatedMessage id="course.header" />
            </InputLabel>
            <Select value={selectedCourseId} onChange={handleCourseChange}>
              {allAvailableCourses.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {GetTranslatedValue(c.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>
              <TranslatedMessage id="application.userSelector.examSession" />
            </InputLabel>
            <Select value={selectedExamSessionId} onChange={handleExamSessionChange} fullWidth>
              {filteredExamSessions.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {GetTranslatedValue(e.sessionKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TranslatedMessage id="application.examLocationAndDate.moduleColumn" />
                </TableCell>
                <TableCell>
                  <TranslatedMessage id="application.moduleSelector.examsColumn" />
                </TableCell>
                <TableCell align="right">
                  <TranslatedMessage id="startCertification.moduleSelectionStep.priceColumn" />
                </TableCell>
                <TableCell width={30} />
              </TableRow>
            </TableHead>
            <TableBody>
              {moduleRowProps.length ? (
                moduleRowProps.map((rowProps, index) => <ModuleSelectorRow key={index} {...rowProps} />)
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <TranslatedMessage id="application.moduleSelector.noAvaiableModules" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default ModuleSelector;
